<template>
  <div
    class="helpdesk-sidebar"
    :class="{
      'helpdesk-sidebar--expanded':
        isPanelOpen
    }"
    :style="{
      '--panel-width':
        `${panelWidth}px`,
      '--total-width':
        `${totalWidth}px`,
      background:
        theme.background,
      color:
        theme.color
    }"
  >
    <!-- ============================================== -->
    <!-- ICON RAIL -->
    <!-- ============================================== -->

    <div
      class="helpdesk-sidebar__rail"
      :style="{
        background:
          theme.primary,
        color:
          theme.primaryColor
      }"
    >
      <v-list
        nav
        density="compact"
        class="helpdesk-sidebar__rail-list"
        :style="{
          background:
            theme.primary,
          color:
            theme.primaryColor
        }"
      >
        <v-tooltip
          v-for="item in visibleMenu"
          :key="item.id"
          location="right"
          :text="item.title"
        >
          <template #activator="{ props }">

            <v-list-item
              v-bind="props"
              class="helpdesk-sidebar__rail-item"
              :active="
                selectedRootId ===
                item.id
              "
              @click="
                selectRoot(
                  item
                )
              "
            >
              <template #default>
                <div
                  class="
                    d-flex
                    align-center
                    justify-center
                    w-100
                  "
                >
                  <v-icon
                    :icon="
                      item.icon ||
                      'mdi-circle-outline'
                    "
                    size="24"
                  />
                </div>
              </template>
            </v-list-item>

          </template>
        </v-tooltip>
      </v-list>
    </div>


    <!-- ============================================== -->
    <!-- DETAIL PANEL -->
    <!-- ============================================== -->

    <v-slide-x-transition>
      <div
        v-if="
          isPanelOpen &&
          selectedRoot
        "
        class="helpdesk-sidebar__panel"
        :style="{
          width:
            `${panelWidth}px`,
          background:
            theme.background,
          color:
            theme.color
        }"
      >
        <!-- HEADER -->

        <div
          class="helpdesk-sidebar__panel-header"
          :style="{
            background:
              theme.primary,
            color:
              theme.color
          }"
        >
          <div
            class="
              d-flex
              align-center
              ga-2
              min-width-0
            "
          >
            <v-icon
              :icon="
                selectedRoot.icon
              "
              size="21"
            />

            <strong
              class="
                helpdesk-sidebar__panel-title
              "
              :style="
                sysTypo.sectionTypography.h3
              "
            >
              {{
                selectedRoot.title
              }}
            </strong>
          </div>

          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            size="small"
            @click="closePanel"
          />
        </div>


        <!-- SEARCH -->

        <div
          class="
            helpdesk-sidebar__search
          "
        >
          <v-text-field
            v-model="search"
            :label="
              currentLanguage ===
              'fr'
                ? 'Rechercher'
                : 'Search'
            "
            prepend-inner-icon="
              mdi-magnify
            "
            variant="outlined"
            density="compact"
            clearable
            hide-details
            :style="
              sysTypo.sectionTypography.input
            "
          />
        </div>

        <v-divider />


        <!-- MENU -->

        <div
          class="
            helpdesk-sidebar__content
          "
        >
          <v-list
            nav
            density="compact"
            :style="{
              ...sysTypo.sectionTypography.body,
              background:
                theme.background,
              color:
                theme.color
            }"
          >
            <v-list-item
              v-for="
                item in
                  filteredChildren
              "
              :key="item.id"
              :prepend-icon="
                item.icon ||
                'mdi-circle-small'
              "
              :title="item.title"
              :active="
                isRouteActive(
                  item.to
                )
              "
              link
              @click="
                navigate(
                  item
                )
              "
            />

            <div
              v-if="
                filteredChildren
                  .length === 0
              "
              class="
                helpdesk-sidebar__empty
              "
            >
              <v-icon
                icon="
                  mdi-magnify-close
                "
                size="28"
              />

              <div class="mt-2">
                {{
                  currentLanguage ===
                  'fr'
                    ? 'Aucun élément'
                    : 'No items found'
                }}
              </div>
            </div>
          </v-list>
        </div>


        <!-- RESIZE -->

        <div
          class="
            helpdesk-sidebar__resize
          "
          @pointerdown="
            startResize
          "
        />
      </div>
    </v-slide-x-transition>
  </div>
</template>


<script setup>
import {computed,onBeforeUnmount,ref,watch} from "vue"
import {useRoute,useRouter} from "vue-router"
import {useI18n} from "vue-i18n"
import {useAuthStore,useThemeStore} from "@/store/auth"
import {useSysTypo} from "@/store/useSysTypo"
import {useLangStore} from "@/store/lang"

const emit =
  defineEmits([
    "resize",
    "panel-state"
  ])


const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const themeStore = useThemeStore()
const sysTypo = useSysTypo()
const langStore = useLangStore()
const { t, locale } = useI18n()


// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(
    () =>
      langStore
        .currentLanguage ||
      "en"
  )

watch(
  currentLanguage,
  value => {
    locale.value =
      value
  },
  {
    immediate: true
  }
)


// ============================================================
// THEME
// ============================================================

const theme =
  computed(() => ({
    background:
      themeStore
        .sectionTheme
        ?.cardBodyBg ||
      "#ffffff",

    color:
      themeStore
        .sectionTheme
        ?.cardBodyColor ||
      "#333333",

    primary:
      themeStore
        .sectionTheme
        ?.cardTitleBgColor ||
      "#263238",

    primaryColor:
      themeStore
        .sectionTheme
        ?.cardTitleTextColor ||
      "#ffffff"
  }))


// ============================================================
// MENU
// ============================================================

const menu =
  computed(() => [
    {
      id: "Administration",
      title: t('helpdesk.sidebar.Administration'),
      icon: "mdi-view-dashboard-outline",
      children: [
        {
          id: "helpdesk-dashboard", 
          title: t('helpdesk.sidebar.Dashboard'),
          icon: "mdi-view-dashboard-outline", to: router.resolve({ name: "HelpdeskDashboard", params: { lang: currentLanguage.value }}).href
        },
      ]
    },

    {
      
      id: "ticket-operations",
      title: t("helpdesk.sidebar.TicketOperations"),
      icon: "mdi-ticket-confirmation-outline",
      children: [
        
        {
          id: "MyRequestdashboard-ticket-management",
          title: t("helpdesk.ticketmanagement"),
          icon: "mdi-headset",
          to: router.resolve({
            name: "SupportTicketManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "MyRequestdashboardPendingApprovals",
          title: t("helpdesk.ticketmanagement.pendingapprovals"),
          icon: "mdi-check-circle-outline",
          to: router.resolve({
            name: "MyPendingApprovals",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "approval-and-grouping",
          title: t("helpdesk.ticketmanagement.approvalgrouping"),
          icon: "mdi-account-multiple-check-outline",
          to: router.resolve({
            name: "ApprovalMembership",
            params: { lang: currentLanguage.value }
          }).href
        },
         
      ],
      
    },

             
     {
      id: "workflow-assignment",
      title: t("helpdesk.sidebar.WorkflowAssignment"),
      icon: "mdi-source-branch",
      children: [
 
        {
          id: "support-workflow",
          title: t("helpdesk.sidebar.Supportworkflow"),
          icon: "mdi-flowchart",
          to: router.resolve({
            name: "SupportWorkFlow",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-approval-workflow",
          title: t("helpdesk.sidebar.Supportapprovalworkflow"),
          icon: "mdi-state-machine",
          to: router.resolve({
            name: "SupportApprovalWorkflow",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-assignment-groups",
          title: t("helpdesk.sidebar.AssignmentGroups"),
          icon: "mdi-account-group-outline",
          to: router.resolve({
            name: "SupportAssignmentGroup",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-assignment-rule-management",
          title: t("helpdesk.sidebar.SupportAssignmentRule"),
          icon: "mdi-routes",
          to: router.resolve({
            name: "SupportAssignmentRuleManagement",
            params: { lang: currentLanguage.value }
          }).href
        }
      ]
    },
            
    {
      id: "helpdesk-configuration",
      title: t("helpdesk.sidebar.HelpdeskConfiguration"),
      icon: "mdi-cog-outline",
      children: [
  
        {
          id: "support-case-type-management",
          title: t("helpdesk.sidebar.supportcasetype"),
          icon: "mdi-file-tree",
          to: router.resolve({
            name: "SupportCaseTypeManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-category",
          title: t("helpdesk.sidebar.Supportcategory"),
          icon: "mdi-tag-multiple-outline",
          to: router.resolve({
            name: "SupportCategoryManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-service",
          title: t("helpdesk.sidebar.SupportService"),
          icon: "mdi-lifebuoy",
          to: router.resolve({
            name: "SupportServiceManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-business-hours",
          title: t("helpdesk.sidebar.BusinessHours"),
          icon: "mdi-clock-time-eight-outline",
          to: router.resolve({
            name: "SupportBusinessHoursManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-sla-policy",
          title: t("helpdesk.sidebar.SupportSLApolicy"),
          icon: "mdi-timer-cog-outline",
          to: router.resolve({
            name: "SupportSlaPolicyManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-ci-type",
          title: t("helpdesk.sidebar.SupportCIType"),
          icon: "mdi-shape-outline",
          to: router.resolve({
            name: "SupportConfigurationItemTypeManagement",
            params: { lang: currentLanguage.value }
          }).href
        },
        {
          id: "support-ci",
          title: t("helpdesk.sidebar.SupportCI"),
          icon: "mdi-server",
          to: router.resolve({
            name: "SupportConfigurationItemManagement",
            params: { lang: currentLanguage.value }
          }).href
        }
      ]
    },
    {
      id: "MyRequestdashboard",
      title: t('helpdesk.servicerequestdashboard.requestdashboard'),
      icon: "mdi-headset",

      children: [
        {
          id: "MyRequestdashboard-MyRequesttickets", 
          title: t('helpdesk.servicerequestdashboard.userrequest'),
          icon: "mdi-headset", to: router.resolve({ name: "SupportTicketRequestUser", params: { lang: currentLanguage.value }}).href
        },
         {
          id: "MyRequestdashboard-MyRequest", 
          title: t('helpdesk.servicerequestdashboard.mysupporttickets'),
          icon: "mdi-headset", to: router.resolve({ name: "MySupportTickets", params: { lang: currentLanguage.value }}).href
        },
        


        
      ]
    }
  ])


// ============================================================
// PERMISSION FILTERING
// ============================================================

function canAccessItem(
  item
) {
  if (
    !item
  ) {
    return false
  }

  if (
    item.public === true
  ) {
    return true
  }

  if (
    item.permission &&
    !auth.hasPermission(
      item.permission
    )
  ) {
    return false
  }

  return true
}

function filterByAccess(
  items
) {
  return (
    items || []
  )
    .map(
      item => {
        if (
          !canAccessItem(
            item
          )
        ) {
          return null
        }

        if (
          item.children?.length
        ) {
          const children =
            filterByAccess(
              item.children
            )

          if (
            children.length === 0
          ) {
            return item.to
              ? {
                  ...item,
                  children:
                    undefined
                }
              : null
          }

          return {
            ...item,
            children
          }
        }

        return item
      }
    )
    .filter(Boolean)
}

const visibleMenu =
  computed(
    () =>
      filterByAccess(
        menu.value
      )
  )


// ============================================================
// STATE
// ============================================================

const selectedRootId =
  ref(
    "Administration"
  )

const isPanelOpen =
  ref(true)

const search =
  ref("")


// ============================================================
// CURRENT ROOT
// ============================================================

const selectedRoot =
  computed(() =>
    visibleMenu.value.find(
      item =>
        item.id ===
        selectedRootId.value
    ) ||
    visibleMenu.value[0] ||
    null
  )


// ============================================================
// FILTER
// ============================================================

const filteredChildren =
  computed(() => {
    const children =
      filterByAccess(
        selectedRoot.value
          ?.children ||
        []
      )

    const query =
      String(
        search.value ||
        ""
      )
        .trim()
        .toLowerCase()

    if (!query) {
      return children
    }

    return children.filter(
      item =>
        String(
          item.title ||
          ""
        )
          .toLowerCase()
          .includes(query)
    )
  })


// ============================================================
// ROUTE
// ============================================================

function normalizePath(
  value
) {
  if (!value) {
    return ""
  }

  try {
    return new URL(
      value,
      window.location.origin
    )
      .pathname
      .replace(
        /\/+$/,
        ""
      )
  }
  catch {
    return String(
      value
    )
      .split("?")[0]
      .split("#")[0]
      .replace(
        /\/+$/,
        ""
      )
  }
}


function isRouteActive(
  to
) {
  return (
    normalizePath(
      route.path
    ) ===
    normalizePath(
      to
    )
  )
}


async function navigate(
  item
) {
  if (!item?.to) {
    return
  }

  await router.push(
    item.to
  )
}


function selectRoot(
  item
) {
  if (!item) {
    return
  }

  if (
    selectedRootId.value ===
      item.id &&
    isPanelOpen.value
  ) {
    closePanel()

    return
  }

  selectedRootId.value =
    item.id

  isPanelOpen.value =
    true

  emitWidth()
}


function closePanel() {
  isPanelOpen.value =
    false

  search.value =
    ""

  emitWidth()
}


// ============================================================
// WIDTH
// ============================================================

const RAIL_WIDTH =
  64

const MIN_PANEL_WIDTH =
  220

const MAX_PANEL_WIDTH =
  520

const panelWidth =
  ref(280)

const totalWidth =
  computed(() =>
    isPanelOpen.value
      ? RAIL_WIDTH +
        panelWidth.value
      : RAIL_WIDTH
  )


function emitWidth() {
  const detail = {
    railWidth:
      RAIL_WIDTH,

    panelWidth:
      isPanelOpen.value
        ? panelWidth.value
        : 0,

    totalWidth:
      totalWidth.value,

    isOpen:
      isPanelOpen.value
  }

  emit(
    "resize",
    detail
  )

  emit(
    "panel-state",
    detail
  )
}


// ============================================================
// RESIZE
// ============================================================

const resizing =
  ref(false)

let startX =
  0

let startWidth =
  0


function startResize(
  event
) {
  if (!isPanelOpen.value) {
    return
  }

  event.preventDefault()

  resizing.value =
    true

  startX =
    event.clientX

  startWidth =
    panelWidth.value

  window.addEventListener(
    "pointermove",
    resize
  )

  window.addEventListener(
    "pointerup",
    stopResize
  )
}


function resize(
  event
) {
  if (!resizing.value) {
    return
  }

  const delta =
    event.clientX -
    startX

  panelWidth.value =
    Math.min(
      MAX_PANEL_WIDTH,
      Math.max(
        MIN_PANEL_WIDTH,
        startWidth +
          delta
      )
    )

  emitWidth()
}


function stopResize() {
  resizing.value =
    false

  window.removeEventListener(
    "pointermove",
    resize
  )

  window.removeEventListener(
    "pointerup",
    stopResize
  )

  emitWidth()
}


watch(
  totalWidth,
  emitWidth,
  {
    immediate: true
  }
)


onBeforeUnmount(
  stopResize
)
</script>


<style scoped>
.helpdesk-sidebar {
  display: flex;
  width: 64px;
  height: 100%;
  min-height: 100%;
  overflow: visible;
}

.helpdesk-sidebar--expanded {
  width: var(--total-width);
}

.helpdesk-sidebar__rail {
  width: 64px;
  min-width: 64px;
  height: 100%;
}

.helpdesk-sidebar__rail-list {
  padding: 8px 6px;
}

.helpdesk-sidebar__rail-item {
  width: 52px;
  min-height: 48px !important;
  margin: 4px 0;
  padding: 0 !important;
  border-radius: 5px !important;
}

.helpdesk-sidebar__rail-item
  :deep(
    .v-list-item__content
  ) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.helpdesk-sidebar__panel {
  position: relative;
  height: 100%;
  flex: 0 0 auto;
  border-right:
    1px solid
    rgba(
      0,
      0,
      0,
      0.08
    );
}

.helpdesk-sidebar__panel-header {
  display: flex;
  min-height: 56px;
  padding:
    8px
    10px
    8px
    14px;
  align-items: center;
  justify-content:
    space-between;
}

.helpdesk-sidebar__panel-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow:
    ellipsis;
}

.helpdesk-sidebar__search {
  padding: 12px;
}

.helpdesk-sidebar__content {
  overflow-y: auto;
  overflow-x: hidden;
  max-height:
    calc(
      100dvh -
      160px
    );
}

.helpdesk-sidebar__empty {
  padding: 32px 12px;
  text-align: center;
  opacity: 0.7;
}

.helpdesk-sidebar__resize {
  position: absolute;
  top: 0;
  right: -4px;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
  z-index: 5;
}

.min-width-0 {
  min-width: 0;
}

@media (
  max-width: 959px
) {
  .helpdesk-sidebar__resize {
    display: none;
  }
}
</style>